/**
 * @file index.tsx
 *
 * @fileoverview Component for training organizations.
 */
import { useQuery } from '@apollo/client';
import React, { useContext } from 'react';
import { TrainingOrganizationsParagraph } from '~/jobiqo/types/types';
import { AppContextInterface } from '~/jobiqo/models/app';
import { AppContext } from '~/jobiqo/context/app';
import {
  getTrainingOrganizationsVariables,
  getTrainingOrganizations,
} from '~/jobiqo/components/Landingpage/TrainingOrganizations/graphql';
import { Box } from '~/vcl/atoms/07-box';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Text } from '~/vcl/atoms/13-text/03-text';
import Picture from '~/vcl/atoms/09-picture';
import Slide from '~/vcl/molecules/15-slider';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import styles from './trainingOrgs.module.scss';
import Link from '~/jobiqo/components/Link';

interface LandingPageTrainingOrganizationsProps {
  /**
   * Top intro text for display on the landing page row.
   */
  rowTrainingOrgsInfo: TrainingOrganizationsParagraph;
  /**
   * The size for the container.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

const PictureWrapper = ({ organization }) => {
  const LogoPicture = (
    <Picture
      title="Logo"
      landscapeSrc={organization?.logo?.url}
      alt={organization?.logo?.alt ?? ''}
    />
  );

  return organization?.url ? (
    <Link
      href={organization?.url?.path ?? ''}
      title={organization?.name ?? ''}
      target="_blank"
      rel="nofollow noreferrer"
    >
      {LogoPicture}
    </Link>
  ) : (
    LogoPicture
  );
};

export const TrainingOrganizations = ({
  rowTrainingOrgsInfo,
  maxContainerSize,
}: LandingPageTrainingOrganizationsProps) => {
  const { ORGANIZATIONS } = useFrontendConfig();
  const appContext: AppContextInterface = useContext(AppContext);
  const sliderRows = (rowTrainingOrgsInfo as any).sliderRows ?? 1;
  const trainingOrganizationsSettings =
    ORGANIZATIONS.TRAINING_ORGANIZATIONS_SETTINGS;

  const trainingOrgsSettings = getTrainingOrganizationsVariables(
    appContext,
    ORGANIZATIONS,
  );
  const { error, loading, data } = useQuery(
    getTrainingOrganizations(ORGANIZATIONS),
    {
      variables: trainingOrgsSettings,
    },
  );

  const groupedOrganizations: any[] = [];
  for (
    let i = 0;
    i < data?.organizationProfiles?.pages.length;
    i += sliderRows
  ) {
    groupedOrganizations.push(
      data?.organizationProfiles?.pages.slice(i, i + sliderRows),
    );
  }

  if (error) {
    return null;
  }

  if (loading) {
    return null;
  }

  if (data?.organizationProfiles?.pages?.length === 0) {
    // If we have no companies don't show the component.
    return null;
  }

  // Dynamically adjusts the slides to show based on the number of organizations.
  // Prevents duplicates if the number of organizations is less than the number of slides to show.
  const slidesToShow = Math.min(
    trainingOrganizationsSettings.SLIDES_TO_SHOW,
    data?.organizationProfiles?.pages?.length,
  );
  return (
    <Box
      className={styles.trainingOrganizationsWrapper}
      boxShadow="none"
      borderRadius="0px"
      marginTop={0}
      paddingTop={rowTrainingOrgsInfo.spacing ?? 11}
      paddingBottom={rowTrainingOrgsInfo.spacing ?? 11}
      backgroundColorOverride={
        rowTrainingOrgsInfo.background
          ? `var(--color-${rowTrainingOrgsInfo.background})`
          : `var(--color-white)`
      }
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        {rowTrainingOrgsInfo.introText && (
          <>
            <Text color="primary" fontWeight="bold" textTransform="uppercase">
              {rowTrainingOrgsInfo.introText}
            </Text>
            <Spacing size="medium" />
          </>
        )}
        {rowTrainingOrgsInfo.introTextSecondary && (
          <>
            <Text tag="h2" fontSize={[5, 6]} lineHeight={[5, 6]}>
              {rowTrainingOrgsInfo.introTextSecondary}
            </Text>
            <Spacing size="large" />
          </>
        )}
        <Slide
          slides={groupedOrganizations.map((organizationRow, rowIndex) => {
            return sliderRows > 1 ? (
              <FlexItems flexDirection="column" gap={5} key={rowIndex}>
                {organizationRow.map((organization, index) => (
                  <div key={index} className="jd-training-slider-item-wrapper">
                    <PictureWrapper key={index} organization={organization} />
                  </div>
                ))}
              </FlexItems>
            ) : (
              <FlexItem key={rowIndex} padding={[5, data.spacing ?? 1]}>
                <PictureWrapper organization={organizationRow[0]} />
              </FlexItem>
            );
          })}
          slidesToShow={slidesToShow}
          autoplay={true}
          showPlayPauseButton={
            trainingOrganizationsSettings.SHOW_PLAY_PAUSE_BUTTON
          }
          dots={trainingOrganizationsSettings.SHOW_DOTS}
          responsive={[
            {
              breakpoint: 1024,
              settings: {
                slidesToShow: 4,
              },
            },
            {
              breakpoint: 768,
              settings: {
                slidesToShow: 3,
              },
            },
            {
              breakpoint: 500,
              settings: {
                slidesToShow: 1,
                dots: false,
              },
            },
          ]}
        />
      </Main>
    </Box>
  );
};
export default TrainingOrganizations;
